<script setup>
import { ref, watch, nextTick, onBeforeUnmount, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEventListener } from '@vueuse/core';

import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import LabelPill from 'dashboard/components-next/label/LabelPill.vue';

const props = defineProps({
  labelMenuItems: {
    type: Array,
    default: () => [],
  },
  iconOnly: {
    type: Boolean,
    default: false,
  },
  /** Teleport + fixed positioning so the menu is not clipped by overflow on ancestors. */
  contained: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['updateLabel']);

const { t } = useI18n();

const showDropdown = ref(false);
const buttonRef = ref(null);
const panelFloatingStyle = ref({});

let stopResize;
const stopScrollFns = [];

function getScrollableParents(el) {
  const list = [];
  let parent = el?.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const { overflow, overflowX, overflowY } = style;
    if (
      [overflow, overflowX, overflowY].some(o =>
        ['auto', 'scroll', 'overlay'].includes(o)
      )
    ) {
      list.push(parent);
    }
    parent = parent.parentElement;
  }
  return list;
}

function updatePanelPosition() {
  if (!props.contained || !showDropdown.value) return;
  const btn = buttonRef.value;
  if (!btn) return;

  const margin = 8;
  const b = btn.getBoundingClientRect();
  const card = btn.closest('[data-contact-labels-root]');

  let left;
  let width;

  if (card) {
    const c = card.getBoundingClientRect();
    const maxW = c.right - c.left - 2 * margin;
    width = Math.min(352, Math.max(maxW, 0));
    left = c.left + margin;
    if (left + width > c.right - margin) {
      left = c.right - margin - width;
    }
    left = Math.max(margin, Math.min(left, window.innerWidth - margin - width));
  } else {
    width = Math.min(352, window.innerWidth - 2 * margin);
    left = Math.max(margin, b.right - width);
  }

  const topBound = card ? card.getBoundingClientRect().top + margin : margin;
  const maxHeight = Math.max(120, b.top - topBound - margin);

  panelFloatingStyle.value = {
    left: `${Math.round(left)}px`,
    width: `${Math.round(width)}px`,
    maxHeight: `${Math.round(maxHeight)}px`,
    bottom: `${Math.round(window.innerHeight - b.top + margin)}px`,
  };
}

watch(showDropdown, async isOpen => {
  stopResize?.();
  stopScrollFns.forEach(fn => fn?.());
  stopScrollFns.length = 0;

  if (isOpen && props.contained) {
    await nextTick();
    updatePanelPosition();
    stopResize = useEventListener(window, 'resize', updatePanelPosition);
    const btn = buttonRef.value;
    if (btn) {
      getScrollableParents(btn).forEach(node => {
        stopScrollFns.push(
          useEventListener(node, 'scroll', updatePanelPosition, {
            passive: true,
          })
        );
      });
    }
  }
});

onBeforeUnmount(() => {
  stopResize?.();
  stopScrollFns.forEach(fn => fn?.());
});

const dropdownClass = computed(() => {
  if (props.contained) {
    return 'z-[200] min-w-0 overflow-y-auto';
  }
  return 'bottom-full z-[200] mb-2 max-h-[min(28rem,65vh)] min-w-[16rem] w-[min(22rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)] overflow-y-auto ltr:right-0 ltr:left-auto rtl:left-0 rtl:right-auto';
});

function pillForItem(item) {
  if (item.pillLabel) return item.pillLabel;
  const color = item.thumbnail?.color || '#64748b';
  return {
    title: item.label,
    color,
    emoji: item.thumbnail?.emoji || '',
  };
}
</script>

<template>
  <div class="relative">
    <button
      ref="buttonRef"
      type="button"
      class="rounded-lg outline-dashed outline-1 outline-n-slate-6 hover:bg-n-alpha-2"
      :class="[
        iconOnly
          ? 'flex size-8 shrink-0 items-center justify-center'
          : 'flex h-6 items-center gap-1 px-2 py-1',
        { 'bg-n-alpha-2': showDropdown },
      ]"
      @click="showDropdown = !showDropdown"
    >
      <span class="i-lucide-plus" />
      <span v-if="!iconOnly" class="text-sm text-n-slate-11">
        {{ t('LABEL.TAG_BUTTON') }}
      </span>
    </button>
    <Teleport to="body" :disabled="!contained">
      <DropdownMenu
        v-if="showDropdown"
        v-on-clickaway="() => (showDropdown = false)"
        leading-visual-only
        :position-strategy="contained ? 'fixed' : 'absolute'"
        :floating-styles="contained ? panelFloatingStyle : undefined"
        :menu-items="labelMenuItems"
        show-search
        :class="dropdownClass"
        @action="emit('updateLabel', $event)"
      >
        <template #thumbnail="{ item }">
          <LabelPill
            :label="pillForItem(item)"
            dense
            class="max-w-full min-w-0"
          />
        </template>
      </DropdownMenu>
    </Teleport>
  </div>
</template>
